import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ViaviTableComponent } from '@nrwl/viavi-tabulator';
import { Subject } from 'rxjs';
import { DataSourceService } from '../../service/datatsource.service';
import {
  DevicesService,
  DialogService,
  FusionTransformationService,
  NotificationService,
  TabulatorService
} from '@nrwl/common-lib';
import { takeUntil } from 'rxjs/operators';
import { ControllerApiService } from '@nrwl/test-setup';

@Component({
  selector: 'nrwl-downloads-settings',
  templateUrl: './downloads-settings.component.html',
  styleUrls: ['./downloads-settings.component.scss']
})
export class DownloadsSettingsComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild(ViaviTableComponent) flowsTable: ViaviTableComponent;
  isLoading = false;
  title = 'Downloads';
  table: any;
  logsTable: any;
  dataAvailable = false;
  pmFlowsData: any;
  isCheckBoxEnabled = false;
  serverConfig = [];
  lastPMFlowAuditUpdate: any;
  showPMAuditResults = false;
  pmAuditList: any;
  licenses: any;
  intervalId: any;
  isPolling = false;
  hasPolledOnce = false;
  isActionsDisabledList = true;
  pmType: string;
  dataSource = [];

  private readonly unsubscribe: Subject<void> = new Subject();

  constructor(
    private readonly dataSourceService: DataSourceService,
    private readonly notificationService: NotificationService,
    private readonly tabulatorService: TabulatorService,
    private dialogService: DialogService,
    private fusionTransformationService: FusionTransformationService,
    private controllerApiService: ControllerApiService,
    private readonly deviceService: DevicesService
  ) {
    this.table = this.tabulatorService.commonTableConfiguration();
    this.table.persistenceID = 'downloads-settings';
    this.table.persistence.sort = true;
    this.table.persistence.page = true;
    this.table.initialSort = [{ column: 'lastAuditTime', dir: 'desc' }];
    // Always set table height
    this.table.height = 500;
    this.table.rowHeight = 45;
    this.logsTable = this.tabulatorService.commonTableConfiguration();
    this.logsTable.persistenceID = 'agent-logs';
    this.logsTable.persistence.sort = true;
    this.logsTable.persistence.page = true;
    this.logsTable.initialSort = [{ column: 'name', dir: 'asc' }];
    // Always set.logsTable height
    this.logsTable.height = 500;
    this.logsTable.rowHeight = 45;
  }

  ngOnInit(): void {
    this.isLicense();
  }

  isLicense(): void {
    this.controllerApiService
      .getLicense()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(
        response => {
          this.licenses = response;
        },
        error => {}
      );
  }

  isTWAMPInitiatorLicensePresent(): any {
    if (this.licenses && this.licenses.data.licenses) {
      const licenseFound = this.licenses.data.licenses.find(
        o => o.featureName === 'EAX-TWAMP-MA' || o.featureName === 'EAX-TWAMP-QT600'
      );
      if (licenseFound) {
        return true;
      }
    }
    return false;
  }

  isY1731LicensePresent(): any {
    // if (this.licenses && this.licenses.data.licenses) {
    //   const licenseFound = this.licenses.data.licenses.find(o => o.featureName === 'EAX-Y1731-MA');
    //   if (licenseFound) {
    //     return true;
    //   }
    // }
    return false;
  }

  emitPMFlowAudit(): void {
    // Clear any existing interval before starting a new one
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }

    this.intervalId = setInterval(() => {
      // Only proceed if no API call is currently in progress
      if (!this.isPolling) {
        this.isPolling = true;

        this.dataSourceService
          .emitPMFlowAudit()
          .pipe(takeUntil(this.unsubscribe))
          .subscribe(
            response => {
              this.updateFlowAuditTable(response.data);
              // Check if both statuses are no longer 'inprogress'
              const twampDone = response.data.twamp.status !== 'inprogress';
              const y1731Done = response.data.y1731.status !== 'inprogress';
              if (twampDone && y1731Done) {
                clearInterval(this.intervalId);
              }
              this.isPolling = false; // Reset the flag after success
            },
            () => {
              this.notificationService.onFail('Fetching PM Flow list failed.');
              this.isPolling = false; // Reset the flag on error
            }
          );
      }
    }, 3000);
  }

  emitPMFlowAuditOnce(changeStatus = false): void {
    this.isLoading = true;
    this.dataSourceService
      .emitPMFlowAudit()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(
        response => {
          this.isLoading = false;
          if (response.data?.[this.pmType] && changeStatus) {
            //changing to intermediate state as inprogress
            response.data[this.pmType].status = 'inprogress';
          }
          this.updateFlowAuditTable(response.data);
        },
        () => {
          this.isLoading = false;
        }
      );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  updateFlowAuditTable(response: any): void {
    this.lastPMFlowAuditUpdate = response;
    if (
      response &&
      (this.flowAuditDefined(response.twamp) || this.flowAuditDefined(response.y1731))
    ) {
      this.showPMAuditResults = true;
      this.pmAuditList = this.transformPMAuditData(response);
      const imagesData = this.fusionTransformationService.transformData(
        'downloads-settings',
        this.pmAuditList
      );

      if (response.twamp.status === 'success' || response.twamp.status === 'idle') {
        this.isActionsDisabledList = false;
      } else {
        this.isActionsDisabledList = true;
      }
      imagesData.data.forEach(data => {
        data.actions.forEach(actionGroup => {
          actionGroup.actions.forEach(action => {
            if (action.id === 'download' || action.id === 'remove') {
              action.disable = this.isActionsDisabledList;
            }
          });
        });
      });
      this.table.columns = imagesData.column;
      this.table.data = imagesData.data;
      this.table = Object.assign({}, this.table);
    } else {
      this.showPMAuditResults = false;
    }
  }

  formatTimestamp(timestamp: string | number): string {
    return new Date(Number(timestamp)).toLocaleString('en-US', { hour12: false });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  flowAuditDefined(test: any): boolean {
    return test && (test.status === 'inprogress' || test.fileModifyTime || test.lastAuditTime);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  transformPMAuditData(data: any): any {
    const temp = [];

    if (data && data.twamp && this.flowAuditDefined(data.twamp)) {
      temp.push(this.getPMAuditObject(data.twamp, 'TWAMP'));
    }
    if (data && data.y1731 && this.flowAuditDefined(data.y1731)) {
      temp.push(this.getPMAuditObject(data.y1731, 'Y1731'));
    }

    return temp;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  getPMAuditObject(item: any, type: string): any {
    return {
      id: item.fileName,
      type: type,
      fileName: item.fileName,
      filePath: item.filePath,
      fileModifyTime: item.status === 'idle' ? this.formatTimestamp(item.fileModifyTime) : null,
      lastAuditTime: item.status === 'idle' ? this.formatTimestamp(item.lastAuditTime) : null,
      status: item.status === 'inprogress' ? 'Export in progress' : item.verdict,
      progressStatus: this.getPMAuditStatus(item),
      message:
        item.status === 'inprogress'
          ? ''
          : item.status === 'idle' && item.verdict === 'success' && !item.message
          ? 'Export completed successfully'
          : item.message,
      resultsTime: item.previousJobResultSummary ? item.previousJobResultSummary.endTime : null
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  getPMAuditStatus(rowData: any): any {
    if (rowData && rowData.status === 'inprogress') {
      return {
        label: 'pm_audit_file_not_available',
        icon: 'progress-circular',
        iconcolor: 'light-blue',
        iconText: 'Export in progress'
      };
    } else if (
      rowData &&
      rowData.status === 'idle' &&
      rowData.verdict &&
      rowData.verdict === 'success'
    ) {
      return {
        label: rowData.fileModifyTime ? '' : 'pm_audit_file_not_available',
        icon: 'check_circle',
        iconcolor: 'light-green'
      };
    } else if (
      rowData &&
      rowData.status === 'idle' &&
      rowData.verdict &&
      rowData.verdict === 'error'
    ) {
      return {
        label: rowData.fileModifyTime ? '' : 'pm_audit_file_not_available',
        icon: 'clear',
        iconcolor: 'light-red'
      };
    } else {
      return {
        label: rowData.fileModifyTime ? '' : 'pm_audit_file_not_available',
        icon: 'remove',
        iconcolor: 'gray'
      };
    }
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  onActionClick(action: any): any {
    this.isLoading = true;
    switch (action.label) {
      case 'download':
        this.downloadFile(action.rowData);
        break;
      case 'remove':
        this.showDeleteConfirm(action.rowData); // Call the method for 'Remove'
        break;
      default:
        console.warn('Unknown action:', action.label);
    }
  }

  auditFlows(pmType: string): void {
    this.pmType = pmType;
    this.emitPMFlowAuditOnce(true);
    this.dataSourceService
      .auditPMFlows(pmType)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(
        response => {
          if (response.status === 'error') {
            this.notificationService.onFail(
              `${pmType.toUpperCase()} PM export did not start: ${response.data.message}`
            );
            console.error(`Export PM flows for ${pmType} has failed. Response = `, response);
          } else {
            // Handle success case if needed
            this.pmFlowsData = response;
            this.emitPMFlowAudit();
          }
        },
        error => {
          this.notificationService.onFail(
            `${pmType.toUpperCase()} PM export did not start: ${error.error.data.message}`
          );
        }
      );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  downloadFile(data: any): void {
    this.isLoading = false;
    const auditDLAnchor = document.createElement('a');
    auditDLAnchor.href = `/${data.filePath}`; //https://fusion-ctl-10.ral.ds.jdsu.net/${data.filePath}
    auditDLAnchor.download = data.fileName; // Set the file name here

    // Append to the DOM for the click to trigger properly
    document.body.appendChild(auditDLAnchor);
    auditDLAnchor.click();

    // Clean up the anchor element after the click
    document.body.removeChild(auditDLAnchor);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  showDeleteConfirm(data: any): void {
    this.isLoading = false;
    const params = {
      id: 'uploadsFieldMapping.delete',
      alternateTitle: `Remove ${data.id} ?`,
      callBackFnParams: data,
      callBackService: this,
      callBackFuntion: this.callBackRemoveFunction
    };
    this.dialogService.showDialog('confirm', 'body', params);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  callBackRemoveFunction(service: any, params: any): void {
    service.showRemoveFileConfirm(params);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  showRemoveFileConfirm(data: any): any {
    this.isLoading = true;
    const pmType = data.type && data.type.indexOf('TWAMP') > -1 ? 'twamp' : 'y1731';
    this.dataSourceService
      .removePMAuditFile(pmType)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(
        response => {
          this.isLoading = false;
          this.notificationService.onSuccess('File removed Successfully');
          this.emitPMFlowAuditOnce();
        },
        error => {
          this.isLoading = false;
          this.notificationService.onFail('Unable to delete the file. Please try again.');
        }
      );
  }

  ngAfterViewInit(): void {
    this.emitPMFlowAuditOnce();
    this.dataAvailable = true;
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
  }

  // eslint-disable-next-line
  onTabChange(event: any): void {
    if (event.index === 1 && this.dataSource.length === 0) {
      this.dataSource = [];
      this.isLoading = true;
      this.deviceService
        .getAgentLogsDetail$()
        .pipe(takeUntil(this.unsubscribe))
        .subscribe(
          response => {
            if (response) {
              this.isLoading = false;
              this.dataSource = [...response.logs];
              this.dataSource.forEach(item => {
                const agent = this.deviceService.findById(item.maId);
                item.name = agent?.['deviceData']?.deviceName;
              });
              const filterData = this.dataSource.filter(item => item.name !== undefined);
              const logsData = this.fusionTransformationService.transformData(
                'agent-logs',
                filterData
              );
              this.logsTable.columns = logsData.column;
              this.logsTable.data = logsData.data;
              this.logsTable = Object.assign({}, this.logsTable);
            }
          },
          error => {
            this.isLoading = false;
          }
        );
    } else {
      this.isLoading = false;
    }
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  downloadAgentLog(url: string): void {
    const logAnchor = document.createElement('a');
    logAnchor.href = url;
    // Append to the DOM for the click to trigger properly
    document.body.appendChild(logAnchor);
    logAnchor.click();

    // Clean up the anchor element after the click
    document.body.removeChild(logAnchor);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  onTimestampClick(data: any): void {
    if (data && data.action === 'logs') {
      this.downloadAgentLog(data?.url);
    }
  }
}
